<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<span>
				<q-icon name="far fa-newspaper" class="q-mb-sm" /> {{ $t(title_news) }}

				<q-tooltip anchor="bottom right" self="top left">
					{{ $t('news_help') }}
				</q-tooltip>
			</span>
		</div>

		<q-table
			:data="news"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					icon="far fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>
				<div class="col" />
				<q-btn
					color="secondary"
					icon="far fa-plus"
					:label="$t('new_f')"
					@click="$router.push({ name: 'admin_news_edit', params: { id: 0 } })"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props">
					{{ props.row.title }}
				</q-td>

				<q-td key="permission" :props="props">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantity(props.row.permission)"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
								{{ $t('areas') }}:
								<span
									v-for="(area, index) in props.row.permission.areas"
									:key="index"
								>
									{{ getArea(area) }}
									<span v-if="props.row.permission.areas.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.user_groups.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('user_groups') }}:
								<span
									v-for="(user_group, index) in props.row.permission.user_groups"
									:key="index"
								>
									{{ getUserGroup(user_group) }}
									<span v-if="props.row.permission.user_groups.length - 1 > index"
										>,
									</span>
								</span>
							</p>
							<p
								v-if="props.row.permission.profiles.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('profiles') }}:
								<span
									v-for="(profile, index) in props.row.permission.profiles"
									:key="index"
								>
									{{ getUserProfile(profile) }}
									<span v-if="props.row.permission.profiles.length - 1 > index"
										>,
									</span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="tags" :props="props">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantityTags(props.row.tags)"
						no-ripple
					>
						<q-tooltip v-if="props.row.tags.length > 0">
							<p class="tooltip q-mb-sm">
								{{ $t('tags') }}:
								<span v-for="(tag, index) in props.row.tags" :key="index">
									{{ getTag(tag) }}
									<span v-if="props.row.tags.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="publish_date" :props="props">
					{{ fDate(props.row.publish_date) }}
				</q-td>

				<q-td key="unpublish_date" :props="props">
					{{ fDate(props.row.unpublish_date) }}
				</q-td>

				<q-td key="updated_date" :props="props">
					{{ fDate(props.row.updated_date) }}
				</q-td>

				<q-td key="status" :props="props" auto-width>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_news_edit',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'news', 'admin'] },

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			news: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: true,
				},
				{
					name: 'permission',
					required: true,
					label: 'permission',
					align: 'left',
					field: 'permission',
					sortable: false,
				},
				{
					name: 'tags',
					required: true,
					label: 'tags',
					align: 'left',
					field: 'tags',
					sortable: false,
				},
				{
					name: 'publish_date',
					required: true,
					label: 'publish_date',
					align: 'left',
					field: 'publish_date',
					sortable: true,
				},
				{
					name: 'unpublish_date',
					required: true,
					label: 'unpublish_date',
					align: 'left',
					field: 'unpublish_date',
					sortable: true,
				},
				{
					name: 'updated_date',
					required: true,
					label: 'date',
					align: 'left',
					field: 'updated_date',
					sortable: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			filter: '',
			action_id: '',
			confirm_remove: false,
			timezone: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			tags: 'registration_lists/tags',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			id_user_language_selected: 'common/id_user_language_selected',
			num_rows: 'datatable/num_rows',
			timezones: 'registration_lists/timezones',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		title_news() {
			if (this.id_user_client_selected) {
				let obj = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj) {
					return obj.title_news == '' ? 'news' : obj.title_news
				}
			}
		},
	},

	methods: {
		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get('/admin_news/news/?' + this.$qs.stringify(data), this.getHeader)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count
					this.news = []

					response.data.results.forEach((result) => {
						let permission = {}

						permission.areas = result.areas
						permission.profiles = result.profiles
						permission.user_groups = result.user_groups

						this.news.push({
							id: result.id,
							title: result.title,
							permission: permission,
							tags: result.tags,
							publish_date: result.publish_date,
							unpublish_date: result.unpublish_date,
							updated_date: result.updated_date,
							status: result.status,
							actions: result.id,
							timezone: result.timezone,
						})
					})
					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_news/news/${this.action_id}/`, this.getHeader)
				.then(() => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getStatus(status) {
			if (status == 1) return 'published_f'
			else if (status == 2) return 'unpublished_f'
			else if (status == 3) return 'deleted_f'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
		},

		getColorStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
		},

		getArea(area) {
			let obj = this.areas.find((obj) => {
				return obj.value === area
			})
			if (obj) return obj.label
			else return ''
		},

		getUserGroup(user_group) {
			let obj = this.user_groups.find((obj) => {
				return obj.value === user_group
			})
			if (obj) return obj.label
			else return ''
		},

		getUserProfile(profile) {
			let obj = this.profiles.find((obj) => {
				return obj.value === profile
			})
			if (obj) return obj.label
			else return ''
		},

		getTag(tag) {
			let obj = this.tags.find((obj) => {
				return obj.value === tag
			})
			if (obj) return obj.label
			else return ''
		},

		getQuantity(permission) {
			return (
				permission.areas.length + permission.user_groups.length + permission.profiles.length
			)
		},

		getQuantityTags(tags) {
			return tags.length > 0 ? tags.length : '0'
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY HH:mm:ss'
						break
					default:
						format = 'MM-DD-YYYY HH:mm:ss'
						break
				}

				return date.formatDate(d, format)
			}
		},

		fetchTimezones() {
			let values = []
			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})
					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')
			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},
	},

	created() {
		this.fetchTimezones()
		this.loadPagination()
		this.fetchLists()
	},
}
</script>

<style lang="stylus" scoped></style>
